import CheckIcon from "@mui/icons-material/Check";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { Button, Dialog, TextField } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { VendorTicketDetail } from "../../../../@types/DRO/WarrantyTicketsResponse";
import { WarrantyTicketsApi } from "../../../../api/WarrantyTickets/WarrantyTicketsApi";
import { useLoading } from "../../../../views/Loader/LoadingContext";

interface RejectDialogProps {
  open: boolean;
  row: VendorTicketDetail | null;
  onClose: () => void;
  onSuccess: () => void;
}

const RejectDialog: React.FC<RejectDialogProps> = ({ open, row, onClose, onSuccess }) => {
  const { setLoading } = useLoading();
  const [step, setStep] = useState<"select-type" | "upload-image">("select-type");
  const [rejectionType, setRejectionType] = useState<"missing" | "damaged" | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [rejectionReasons, setRejectionReasons] = useState<{ id: number; rejectionReason: string }[]>([]);
  const [selectedReasonId, setSelectedReasonId] = useState<number | "other" | null>(null);
  const [remarks, setRemarks] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && step === "upload-image") {
      WarrantyTicketsApi.api.getRejectionReasons().then(setRejectionReasons).catch(console.error);
    }
  }, [open, step]);

  const handleClose = () => {
    setStep("select-type");
    setRejectionType(null);
    setImageFile(null);
    setSelectedReasonId(null);
    setRemarks("");
    onClose();
  };

  const handleConfirm = async () => {
    const trimmedRemarks = remarks.trim();
    const isOtherReason = selectedReasonId === "other";
    const hasSelectedReason = typeof selectedReasonId === "number";

    if (!row || (isOtherReason && !trimmedRemarks) || (!isOtherReason && !hasSelectedReason)) return;
    handleClose();
    setLoading(true);
    try {
      await WarrantyTicketsApi.api.warrantyTicketAction({
        warrantyActionItems: [
          {
            uid: row.uid,
            action: "REJECT",
            imageId: row.imageId,
            isPartDamaged: rejectionType === "damaged",
            rejectionReasonId: hasSelectedReason ? selectedReasonId : undefined,
            remarks: isOtherReason ? trimmedRemarks : undefined,
          },
        ],
      });
      setSuccessOpen(true);
      onSuccess();
    } catch (error) {
      console.error("Error rejecting ticket", error);
    }
    setLoading(false);
  };

  const handleRejectDirect = async () => {
    if (!row) return;
    handleClose();
    setLoading(true);
    try {
      await WarrantyTicketsApi.api.warrantyTicketAction({
        warrantyActionItems: [{ uid: row.uid, action: "REJECT", imageId: row.imageId }],
      });
      setSuccessOpen(true);
      onSuccess();
    } catch (error) {
      console.error("Error rejecting ticket", error);
    }
    setLoading(false);
  };

  return (
    <>
      {/* Step 1 — Select type */}
      <Dialog
        open={open && step === "select-type"}
        onClose={handleClose}
        PaperProps={{ sx: { borderRadius: 3, p: 3, width: 300 } }}>
        <p className="text-center text-gray-800 font-medium mb-5 leading-snug">
          Please select if the Part is<br />Damaged or Missing?
        </p>
        <div className="flex items-center justify-center gap-3">
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setRejectionType("missing");
              setStep("upload-image");
            }}
            sx={{ textTransform: "none", borderRadius: 5, borderColor: "#E57373", color: "#E57373", "&:hover": { borderColor: "#EF4444", bgcolor: "#FFF5F5" } }}>
            Part Missing
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setRejectionType("damaged");
              setStep("upload-image");
            }}
            sx={{ textTransform: "none", borderRadius: 5, borderColor: "#E57373", color: "#E57373", "&:hover": { borderColor: "#EF4444", bgcolor: "#FFF5F5" } }}>
            Part Damaged
          </Button>
        </div>
      </Dialog>

      {/* Step 2 — Upload image + remarks */}
      <Dialog
        open={open && step === "upload-image"}
        onClose={handleClose}
        PaperProps={{ sx: { borderRadius: 3, p: 3, width: 340 } }}>
        <p className="text-base font-semibold text-gray-800 mb-4">
          {rejectionType === "missing" ? "Reject - Part Missing" : "Reject - Damage Image"}
        </p>

        <div className="border border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center gap-2 mb-3 bg-gray-50">
          {imageFile ? (
            <div className="flex items-center gap-2 w-full justify-between bg-white border border-gray-200 rounded px-3 py-2">
              <span className="text-sm text-gray-600 truncate">{imageFile.name}</span>
              <button onClick={() => setImageFile(null)} className="text-red-500 hover:text-red-700 flex-shrink-0">
                ✕
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1 text-center">
              <ImageOutlinedIcon sx={{ fontSize: 40, color: "#93C5FD" }} />
              <p className="text-xs text-gray-400 leading-snug">
                Open In App Camera From 'Create Video' Or<br />Upload Files From Your Gallery
              </p>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => setImageFile(e.target.files?.[0] ?? null)}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<CloudUploadOutlinedIcon />}
            onClick={() => fileInputRef.current?.click()}
            sx={{ textTransform: "none", bgcolor: "#2563EB", "&:hover": { bgcolor: "#1D4ED8" }, mt: 1 }}>
            Upload Image
          </Button>
        </div>

        <p className="text-sm font-semibold text-gray-700 mb-3">Select Reason For Rejection</p>
        <div className="mb-4 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
          {rejectionReasons.length > 0 ? (
            <ul className="space-y-2">
              {rejectionReasons.map((reason) => (
                <li key={reason.id} className="flex items-center">
                  <input
                    type="radio"
                    id={`reason-${reason.id}`}
                    name="rejection-reason"
                    value={reason.id}
                    checked={selectedReasonId === reason.id}
                    onChange={() => setSelectedReasonId(reason.id)}
                    className="mr-2 cursor-pointer"
                  />
                  <label htmlFor={`reason-${reason.id}`} className="text-sm text-gray-700 cursor-pointer flex-1">
                    {reason.rejectionReason}
                  </label>
                </li>
              ))}
              <li className="flex items-center">
                <input
                  type="radio"
                  id="reason-other"
                  name="rejection-reason"
                  value="other"
                  checked={selectedReasonId === "other"}
                  onChange={() => {
                    setSelectedReasonId("other");
                    setRemarks("");
                  }}
                  className="mr-2 cursor-pointer"
                />
                <label htmlFor="reason-other" className="text-sm text-gray-700 cursor-pointer flex-1">
                  Other
                </label>
              </li>
            </ul>
          ) : (
            <p className="text-sm text-gray-400">Loading reasons...</p>
          )}
        </div>

        {selectedReasonId === "other" && (
          <>
            <TextField
              label="Remarks"
              placeholder="Add remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              sx={{ mb: 2, bgcolor: "white" }}
            />

            <p className="text-xs text-gray-500 mb-4">
              Enter remarks for the Other reason.
            </p>
          </>
        )}

        <div className="flex items-center justify-end gap-3">
          <Button
            variant="outlined"
            size="small"
            onClick={() => setStep("select-type")}
            sx={{ textTransform: "none", borderColor: "#ccc", color: "#333", "&:hover": { borderColor: "#999" } }}>
            No, Go Back
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={selectedReasonId === "other" ? !remarks.trim() : !selectedReasonId}
            onClick={handleConfirm}
            sx={{
              textTransform: "none",
              bgcolor: selectedReasonId ? "#EF4444" : undefined,
              "&:hover": { bgcolor: selectedReasonId ? "#DC2626" : undefined },
            }}>
            Reject Request
          </Button>
        </div>
      </Dialog>

      {/* Success */}
      <Dialog
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, p: 4, width: 280 } }}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center">
            <CheckIcon sx={{ color: "white", fontSize: 36 }} />
          </div>
          <p className="text-gray-800 font-semibold text-center">Warranty Request Rejected!</p>
        </div>
      </Dialog>
    </>
  );
};

export default RejectDialog;
